// vendors
import React from "react"
import { graphql } from "gatsby"
import { css } from "@emotion/core"
import styled from "@emotion/styled"
import GoogleMapReact from "google-map-react"

// components
import Layout from "../components/Layout"
import Hero from "../components/Hero"
import Button from "../components/Button/Button"
import SidebarLayout from "../components/SidebarLayout/SidebarLayout"
import BlockContent from "../components/BlockContent/BlockContent"
import LinkGroup from "../components/LinkGroup"
import Link from "../components/Link"
import Seo from "../components/Seo"

// utils
import { colors, fontSizes } from "../styles/variables"
import formatTime from "../utils/format-time"
import mediaQuery from "../utils/media-query"
import mapStyle from "../utils/map-style"
import wrapper from "../utils/wrapper"

// images
import VectorCalendar from "../images/VectorCalendar"
// import iconCalendar from "../images/icon-calendar.svg"
import illustrationTicket from "../images/illustration-ticket.svg"
import VectorMap from "../images/VectorMap"
// import iconMap from "../images/icon-map.svg"
import PinPoint from "../images/VectorPinPoint"

const Card = styled.div`
  font-weight: bold;
  font-size: ${fontSizes[2] / fontSizes[0]}em;
  line-height: ${fontSizes[3] / fontSizes[2]};
  max-width: 500px;

  svg {
    width: ${fontSizes[7] / fontSizes[2]}em;
    margin-bottom: 1em;
  }

  p {
    margin: 0;
  }

  ${mediaQuery.greaterThen(fontSizes.breakpoint)} {
    font-size: ${fontSizes[4] / fontSizes[1]}em;
  }
`

const breakpoint = 1200

const EventTemplate = ({ data, path, ...props }) => {
  const event = data.sanityEvent || {}

  const slides = (event.featuredImage || []).map((slide) => slide.asset.fluid)
  const bgColor = event.color ? event.color.color.hex : colors.dullYellow

  const fromDate = event.fromDate ? new Date(event.fromDate) : null
  const toDate = event.toDate ? new Date(event.toDate) : null

  const sidebar = (
    <div
      css={css`
        display: flex;
        align-items: center;

        ${mediaQuery.lessThen(breakpoint)} {
          justify-content: center;
        }

        ${mediaQuery.greaterThen(breakpoint)} {
          ${wrapper.paddingLeft()}
          ${wrapper.paddingRight()}
          height: calc(100vh - 250px);
          position: sticky;
          top: 250px;
          margin-bottom: 7.25em;
        }
      `}
    >
      <div
        css={css`
          text-align: center;
          max-width: 215px;
        `}
      >
        <img
          src={illustrationTicket}
          alt=""
          css={css`
            max-width: 113px;
            margin-bottom: 1em;
          `}
        />
        <p
          css={css`
            font-weight: bold;
            font-size: ${fontSizes[4] / fontSizes[0]}em;
            line-height: ${fontSizes[5] / fontSizes[4]};

            ${mediaQuery.greaterThen(fontSizes.breakpoint)} {
              font-size: ${fontSizes[5] / fontSizes[1]}em;
            }
          `}
        >
          Réservez votre&nbsp;place maintenant!
        </p>

        <p>
          <Button tag="a" href={event.eventBriteLink} full>
            S'inscrire
          </Button>
        </p>
      </div>
    </div>
  )
  return (
    <Layout>
      <Seo
        description={event.meta.description}
        lang="fr"
        keywords={event.meta.keyword}
        title={event.meta.title}
      />

      <article>
        <Hero
          slides={slides}
          title={event.title}
          online={event.online}
          bgColor={bgColor}
        >
          <div
            css={css`
              display: flex;
              justify-content: space-around;
              text-align: center;
              flex-wrap: wrap;
              align-items: flex-start;
              margin: -0.5em;

              > ${Card} {
                margin: 0.5em;
              }
            `}
          >
            <Card>
              <div
                css={css`
                  height: 76px;

                  ${mediaQuery.greaterThen(fontSizes.breakpoint)} {
                    height: 115px;
                  }
                `}
              >
                <VectorCalendar />
              </div>

              <p>
                {event.upcoming ? (
                  <span>À venir</span>
                ) : (
                  <>
                    {fromDate?.toLocaleDateString("fr", { weekday: "long" })}
                    <br />
                    {fromDate?.toLocaleDateString("fr", {
                      day: "2-digit",
                      month: "long",
                      year: "numeric",
                    })}
                    <br />
                    {formatTime(fromDate)}
                    {toDate && <span> à {formatTime(toDate)}</span>}
                  </>
                )}
              </p>

              {event.online && event.onlineUrl && (
                <Button
                  full
                  tag="a"
                  to={event.onlineUrl}
                  rel="noopener noreferrer"
                  target="_blank"
                  css={css`
                    margin-bottom: 0 !important;
                  `}
                >
                  Se connecter à l'événement
                </Button>
              )}
            </Card>

            {event.placeName && event.address && (
              <Card>
                <div
                  css={css`
                    height: 76px;

                    ${mediaQuery.greaterThen(fontSizes.breakpoint)} {
                      height: 115px;
                    }
                  `}
                >
                  <VectorMap />
                </div>

                <p>
                  {event.placeName}
                  <br />
                  {event.address.street}
                  <br />
                  {event.address.city}
                </p>

                <p>{event.address.local}</p>
              </Card>
            )}

            {event.eventBriteLink && (
              <Card
                css={css`
                  align-self: center;
                  margin-top: 2em;
                  margin-bottom: 2em;
                `}
              >
                <Button tag="a" href={event.eventBriteLink} full>
                  S'inscrire
                </Button>
              </Card>
            )}
          </div>
        </Hero>

        <SidebarLayout
          sidebar={event.eventBriteLink && sidebar}
          breakpoint={breakpoint}
          sideAfter
          css={css`
            margin-top: ${fontSizes[9] / fontSizes[0]}em;
          `}
        >
          <div className="styled-content">
            <BlockContent blocks={event._rawDescription} />
          </div>

          <LinkGroup>
            {event.programation && (
              <Link tag="a" href={event.programation.asset.url} target="_blank">
                Consulter la programmation
              </Link>
            )}

            {event.speaker.length > 0 && (
              <Link to={`${path}/conferenciers`}>
                Voir la liste des conférenciers
              </Link>
            )}
          </LinkGroup>
        </SidebarLayout>

        {event.address && event.address.geopoint && (
          <div
            css={css`
              width: 100vw;
              height: ${(882 / 1920) * 100}vw;
              background-color: tomato;
            `}
          >
            <GoogleMapReact
              bootstrapURLKeys={{
                key: "AIzaSyAyinQJCapFFz5eB9zqgT37c-K5WbLlPu4",
              }}
              defaultCenter={event.address.geopoint}
              defaultZoom={16}
              options={{ styles: mapStyle }}
            >
              <PinPoint
                css={css`
                  display: block;
                  width: ${(74 / 1920) * 100}vw;
                  min-width: 30px;
                  max-width: 74px;
                  position: absolute;
                  transform: translate(-50%, -50%);
                `}
                color={colors.pastelRed}
                lat={event.address.geopoint.lat}
                lng={event.address.geopoint.lng}
              />
            </GoogleMapReact>
          </div>
        )}
      </article>
    </Layout>
  )
}

export default EventTemplate

export const query = graphql`
  query EventTemplateQuery($id: String!) {
    sanityEvent(id: { eq: $id }) {
      _rawDescription
      meta {
        description
        keyword
        slug {
          current
        }
        title
      }
      title
      placeName
      type
      fromDate
      toDate
      upcoming
      eventBriteLink
      online
      onlineUrl
      address {
        city
        geopoint {
          lat
          lng
        }
        local
        province
        street
        zip
      }
      programation {
        asset {
          url
        }
      }
      featuredImage {
        asset {
          fluid {
            src
            srcSet
            srcSetWebp
          }
        }
      }
      speaker {
        name
      }
      color {
        color {
          hex
          alpha
        }
      }
    }
  }
`
